import { DateFormat, getDateRangeText, getDateText } from "../../../helpers/dateHelper";
import { IAddress } from "../../../models/address";
import { IDock } from "../../../models/dock";
import {
    StatsExpensePerDock,
    StatsExpensePerShip,
    StatsExpensePerShipAndDock,
} from "../../../models/stats";
import { CsvStatisticsFilters } from "./filters";

const sanitizeValues = (values: any[]): string[] => {
    return values.map(value => {
        if (value === null || value === undefined) return "";
        if (typeof value === "number") return String(value);
        if (typeof value === "string") return value.trim();
        return String(value);
    });
};

export const getStatsExpensePerShipValues = (
    data: StatsExpensePerShip,
    filters: CsvStatisticsFilters,
    managingCompanies: Map<any, any>,
    owningCompanies: IAddress[],
    docks: IDock[],
): string[] => {
    const managingCompanyId = data.ship.docksCompanyId;
    const owningCompanyId = data.ship.data?.owningCompanyAddressId;

    const values = [
        getDateText(filters.from, DateFormat.CLIENT_DATE),
        getDateText(filters.to, DateFormat.CLIENT_DATE),
        data.ship.name,
        data.ship.eni,
        data.ship.mmsi,
        managingCompanies.get(managingCompanyId),
        owningCompanies?.find(({ docksId }) => docksId === owningCompanyId)?.company,
        data.ship.passengerBeds,
        data.ship.data?.length,

        data.ship.electricityCardNumber,
        data.ship.electricityCardNumberActive ? "Active" : "Inactive",

        data.ship.keyNumber,
        data.ship.keyNumberActive ? "Active" : "Inactive",

        data.stats.arrivalDays,
        data.stats.arrivalCost,

        data.stats.layDays,
        data.stats.layCost,

        data.stats.electricityQuantity,
        data.stats.electricityCost,

        data.stats.waterQuantity,
        data.stats.waterCost,

        data.stats.wasteQuantity,
        data.stats.wasteCost,

        data.stats.serviceSatCount,
        data.stats.serviceSunCount,

        data.stats.serviceCost,
        data.stats.wayFeeCost,

        (data.ship.passengerBeds ?? 0) * (data.stats.arrivalDays + data.stats.layDays),
        data.stats.arrivalDays + data.stats.layDays,
        data.stats.totalCost,
    ];

    const output = sanitizeValues(values);

    // Process docks and append to output
    docks.forEach(dock => {
        const berthing = data.berthings?.find(berthing => berthing.id === dock.id);
        output.push(String(berthing?.count || ""));
    });

    return output;
};

export const getStatsExpensePerDockValues = (
    data: StatsExpensePerDock,
    filters: CsvStatisticsFilters,
    docks: IDock[],
) => {
    const values = [
        getDateText(filters.from, DateFormat.CLIENT_DATE),
        getDateText(filters.to, DateFormat.CLIENT_DATE),
        docks.find(dock => dock.id === data.expenseStats1.id)?.name,

        data.lengthCount.le100,
        data.lengthCount.gt100,
        data.lengthCount.beds,

        data.expenseStats1.arrivalDays,
        data.expenseStats1.arrivalCost,

        data.expenseStats1.layDays,
        data.expenseStats1.layCost,

        data.expenseStats1.electricityQuantity,
        data.expenseStats1.electricityCost,

        data.expenseStats1.waterQuantity,
        data.expenseStats1.waterCost,

        data.expenseStats1.wasteQuantity,
        data.expenseStats1.wasteCost,

        data.expenseStats1.serviceSatCount,
        data.expenseStats1.serviceSunCount,

        data.expenseStats1.serviceCost,
        data.expenseStats1.wayFeeCost,

        data.expenseStats1.arrivalDays + data.expenseStats1.layDays,
        data.expenseStats1.totalCost,
    ];

    return sanitizeValues(values);
};

export const getStatsExpensePerShipAndDockValues = (
    data: StatsExpensePerShipAndDock,
    filters: CsvStatisticsFilters,
) => {
    const values = [
        getDateText(filters.from, DateFormat.CLIENT_DATE),
        getDateText(filters.to, DateFormat.CLIENT_DATE),
        data.dockName,
        data.shipName,
        data.managingCompany,
        data.owningCompany,
        data.arrivalDays,
        data.layDays,
        data.arrivalDays + data.layDays,
    ];
    return sanitizeValues(values);
};

export const getFileName = (statisticTypeName: string, from: number | null, to: number | null) => {
    return `${statisticTypeName.replaceAll(" ", "_")}_${getDateRangeText(
        from,
        to,
        DateFormat.API_DATE,
    ).replaceAll(" ", "")}.csv`;
};
